<template>
  <div class="mt-component-form">
    <vs-row class="my-4 header-flex">
      <h5><i class="dot"></i>점검 대상</h5>
      <div class="header-buttons">
        <!-- Desktop: 항목추가 + 선택삭제 -->
        <vs-button
          v-if="!isPrint && !isReadonly"
          class="desktop-only"
          color="secondary"
          type="border"
          @click="handleAddRow">항목추가</vs-button>
        <!-- PC/Mobile 공통: 선택삭제 -->
        <vs-button
          v-if="!isPrint && !isReadonly"
          class="ml-4"
          color="secondary"
          :disabled="selectedItemsCount === 0"
          @click="handleRemoveRowConfirm">선택삭제</vs-button>
      </div>
    </vs-row>

    <!-- Desktop 테이블 레이아웃 -->
    <div class="desktop-layout">
      <div class="desktop-table-header">
        <div v-if="!isPrint && !isReadonly" class="checkbox-col row-header__item">
          <vs-checkbox v-model="equipAllCheck" @change="changeAllCheck"></vs-checkbox>
        </div>
        <div class="content-col row-header__item">점검대상</div>
        <div class="content-col row-header__item">담당자</div>
        <div class="content-col row-header__item">연락처</div>
        <div class="content-col-wide row-header__item">비고</div>
      </div>

      <!-- 빈 데이터 안내 (PC용) -->
      <div v-if="targetList.length === 0 && !isPrint" class="desktop-empty-message">
        <div class="desktop-empty-content">
          <i class="feather icon-inbox desktop-empty-icon"></i>
          <p class="desktop-empty-text">점검 대상을 추가해주세요</p>
          <vs-button
            color="primary"
            type="filled"
            @click="handleAddRow"
            class="desktop-empty-btn"
            v-if="!isReadonly"
          >
            <i class="feather icon-plus mr-2"></i>
            대상 추가
          </vs-button>
        </div>
      </div>

      <!-- rows -->
      <div
        class="desktop-table-row"
        v-for="(rows, indexRows) in targetList"
        :key="indexRows"
      >
        <div v-if="!isPrint && !isReadonly" class="checkbox-col none-border-center">
          <vs-checkbox v-model="rows.is_checked"></vs-checkbox>
        </div>
        <div class="content-col item-area">
          <ml-input v-if="!isPrint" :readonly="isReadonly" rows="1" v-model="rows.target" class="w-full flex-1 p-1"/>
          <span v-else class="print-input">{{ rows.target }}</span>
        </div>
        <div class="content-col item-area">
          <ml-input v-if="!isPrint" :readonly="isReadonly" rows="1" v-model="rows.director" class="w-full flex-1 p-1"/>
          <span v-else class="print-input">{{ rows.director }}</span>
        </div>
        <div class="content-col item-area">
          <ml-input v-if="!isPrint" :readonly="isReadonly" rows="1" v-model="rows.phone" class="w-full flex-1 p-1"/>
          <span v-else class="print-input">{{ rows.phone }}</span>
        </div>
        <div class="content-col-wide item-area">
          <ml-input v-if="!isPrint" :readonly="isReadonly" rows="1" v-model="rows.note" class="w-full flex-1 p-1"/>
          <span v-else class="print-input">{{ rows.note }}</span>
        </div>
      </div>
    </div>

    <!-- Mobile 카드 레이아웃 -->
    <div class="mobile-layout">
      <!-- 빈 목록 안내 메시지 (모바일용) -->
      <div v-if="targetList.length === 0 && !isPrint" class="mobile-empty-message">
        <div class="mobile-empty-content">
          <i class="feather icon-inbox mobile-empty-icon"></i>
          <p class="mobile-empty-text">점검 대상을 추가해주세요</p>
          <vs-button
            color="primary"
            type="filled"
            @click="handleAddRow"
            class="mobile-empty-btn"
            v-if="!isReadonly"
          >
            <i class="feather icon-plus mr-2"></i>
            첫 번째 대상 추가
          </vs-button>
        </div>
      </div>

      <!-- 모바일 전체 선택 헤더 (아이템이 있을 때만) -->
      <div
        class="mobile-header"
        v-if="!isPrint && !isReadonly && targetList.length > 0"
        @click="toggleAllCheck"
      >
        <vs-checkbox :value="equipAllCheck" />
        <span class="mobile-header-text">전체 선택</span>
      </div>

      <!-- 모바일 카드형 아이템들 -->
      <div class="mobile-cards-container">
        <div
          v-for="(rows, indexRows) in targetList"
          :key="indexRows"
          class="mobile-card"
          :class="{ 'selected': rows.is_checked, 'print-mode': isPrint }"
        >
          <!-- 카드 헤더 (체크박스 + 인덱스 + 삭제 버튼) -->
          <div class="mobile-card-header" v-if="!isPrint">
            <div class="mobile-card-header-left">
              <vs-checkbox v-if="!isReadonly" v-model="rows.is_checked"></vs-checkbox>
              <span class="card-index">{{ indexRows + 1 }}번째 대상</span>
            </div>
            <vs-button
              v-if="!isReadonly"
              class="mobile-delete-btn"
              color="danger"
              type="flat"
              size="small"
              @click="handleDeleteSingleItem(indexRows)"
            >
              삭제
            </vs-button>
          </div>
          <div class="mobile-card-header print-header" v-else>
            <span class="card-index">{{ indexRows + 1 }}번째 대상</span>
          </div>

          <!-- 카드 내용 -->
          <div class="mobile-card-content">
            <!-- 점검대상 -->
            <div class="mobile-field">
              <label class="mobile-field-label">점검대상</label>
              <div class="mobile-field-input">
                <ml-input
                  v-if="!isPrint"
                  rows="1"
                  v-model="rows.target"
                  class="w-full"
                  :readonly="isReadonly"
                />
                <span v-else class="mobile-print-value">{{ rows.target }}</span>
              </div>
            </div>

            <!-- 담당자와 연락처 - 한 줄로 배치 -->
            <div class="mobile-field-row">
              <div class="mobile-field mobile-field-half">
                <label class="mobile-field-label">담당자</label>
                <div class="mobile-field-input">
                  <ml-input
                    v-if="!isPrint"
                    rows="1"
                    v-model="rows.director"
                    class="w-full"
                    :readonly="isReadonly"
                  />
                  <span v-else class="mobile-print-value">{{ rows.director }}</span>
                </div>
              </div>
              <div class="mobile-field mobile-field-half">
                <label class="mobile-field-label">연락처</label>
                <div class="mobile-field-input">
                  <ml-input
                    v-if="!isPrint"
                    rows="1"
                    v-model="rows.phone"
                    class="w-full"
                    :readonly="isReadonly"
                  />
                  <span v-else class="mobile-print-value">{{ rows.phone }}</span>
                </div>
              </div>
            </div>

            <!-- 비고 -->
            <div class="mobile-field">
              <label class="mobile-field-label">비고</label>
              <div class="mobile-field-input">
                <ml-input
                  v-if="!isPrint"
                  rows="1"
                  v-model="rows.note"
                  class="w-full"
                  :readonly="isReadonly"
                />
                <span v-else class="mobile-print-value">{{ rows.note }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 모바일 하단 항목 추가 버튼 (아이템이 있을 때만) -->
      <div class="mobile-add-button-container" v-if="!isPrint && !isReadonly && targetList.length > 0">
        <vs-button
          class="mobile-add-btn"
          color="primary"
          type="filled"
          size="large"
          @click="handleAddRow"
        >
          <i class="feather icon-plus mr-2"></i>
          대상 추가
        </vs-button>
      </div>
    </div>

    <!-- 삭제 확인 팝업 -->
    <vs-popup class="popup-example" title="삭제 확인" :active.sync="showDeleteConfirm">
      <div class="popup-content">
        <p class="delete-message">{{ deleteMessage }}</p>
        <div class="popup-buttons">
          <vs-button
            color="danger"
            type="filled"
            @click="confirmDelete"
          >
            삭제
          </vs-button>
          <vs-button
            color="dark"
            type="flat"
            @click="cancelDelete"
          >
            취소
          </vs-button>
        </div>
      </div>
    </vs-popup>
  </div>
</template>

<script>
export default {
  name: 'TableTargetEquipmentInspection',
  props: {
    list: Array,
    isPrint: Boolean,
    isReadonly: Boolean
  },
  watch: {
    list: {
      immediate: true,
      deep: true,
      handler (value) {
        if (value && value.length > 0) {
          this.targetList = value
        }
      }
    },
  },
  methods: {
    checkForm (value, indexRows, text) {
      if (value.target.checked) {
        this.$emit('update-list-check', indexRows - 1, text)
      } else {
        this.$emit('update-list-check', indexRows - 1, '')
      }
    },
    // 항목추가
    handleAddRow () {
      this.targetList.push({
        is_checked: this.equipAllCheck,
        target: '',
        director: '',
        phone: '',
        note: ''
      })
      this.updateList()
    },
    // 선택삭제 확인
    handleRemoveRowConfirm () {
      const selectedCount = this.targetList.filter(item => item.is_checked).length
      if (selectedCount === 0) {
        this.$vs.notify({
          title: '알림',
          text: '삭제할 항목을 선택해주세요.',
          color: 'warning'
        })
        return
      }

      this.deleteMessage = `선택된 ${selectedCount}개의 대상을 삭제하시겠습니까?`
      this.deleteAction = 'batch'
      this.showDeleteConfirm = true
    },
    // 개별 항목 삭제 확인
    handleDeleteSingleItem (index) {
      this.deleteMessage = `${index + 1}번째 대상을 삭제하시겠습니까?`
      this.deleteAction = 'single'
      this.deleteIndex = index
      this.showDeleteConfirm = true
    },
    // 삭제 확인
    confirmDelete () {
      if (this.deleteAction === 'batch') {
        this.handleRemoveRow()
      } else if (this.deleteAction === 'single') {
        this.handleRemoveSingleRow()
      }
      this.showDeleteConfirm = false
    },
    // 삭제 취소
    cancelDelete () {
      this.showDeleteConfirm = false
      this.deleteAction = null
      this.deleteIndex = null
      this.deleteMessage = ''
    },
    // 선택삭제 실행
    handleRemoveRow () {
      for (let i = 0; i < this.targetList.length; i++) {
        if (this.targetList[i].is_checked) {
          this.targetList.splice(i, 1)
          i--
        }
      }

      this.equipAllCheck = false
      this.updateList()

      this.$vs.notify({
        title: '삭제 완료',
        text: '선택된 대상이 삭제되었습니다.',
        color: 'success'
      })
    },
    // 개별 항목 삭제 실행
    handleRemoveSingleRow () {
      this.targetList.splice(this.deleteIndex, 1)
      this.updateList()

      this.$vs.notify({
        title: '삭제 완료',
        text: '대상이 삭제되었습니다.',
        color: 'success'
      })
    },
    // 일괄 체크
    changeAllCheck () {
      for (const data of this.targetList) {
        data.is_checked = this.equipAllCheck
      }
    },
    // 전체 선택 토글 (모바일 헤더 클릭 시)
    toggleAllCheck () {
      this.equipAllCheck = !this.equipAllCheck
      this.changeAllCheck()
    },
    // 체크박스 직접 클릭 처리
    handleCheckboxClick () {
      // v-model에 의해 equipAllCheck는 이미 변경된 상태
      this.changeAllCheck()
    },
    // update
    updateList () {
      this.$emit('updateList', this.targetList)
    }
  },
  computed: {
    selectedItemsCount () {
      return this.targetList.filter(item => item.is_checked).length
    },
  },
  data () {
    return {
      equipAllCheck: false,
      targetList: [],
      showDeleteConfirm: false,
      deleteMessage: '',
      deleteAction: null,
      deleteIndex: null,
    }
  }
}
</script>

<style scoped lang="scss">
$border: 1px solid #cdcdcd;
$bg: #f9f9f9;
$mobile-breakpoint: 768px;
$mobile-card-bg: #ffffff;
$mobile-card-border: 1px solid #e8e8e8;
$mobile-card-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
$mobile-selected-bg: #f0f9ff;
$mobile-selected-border: 1px solid #3b82f6;

.center-flex {
  display: flex;
  justify-content: center;
  align-items: center;
}

.header-flex {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-buttons {
  display: flex;
  align-items: center;
}

.desktop-only {
  display: inline-flex;
}

.label-text {
  width: 127px;
  font-weight: 500;
  font-size: 14px;
}
.mt-component-form {
  margin-top: 25px;
}
.vs-checkbox-default {
  margin: 0;
}
.ml-group-checkBox ~ .ml-group-checkBox {
  /*margin-left: 15px;*/
  margin: 0;
}

/* 반응형 레이아웃 제어 */
.desktop-layout {
  display: block;
}
.mobile-layout {
  display: none;
}

/* PC용 빈 데이터 안내 메시지 */
.desktop-empty-message {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-top: none;
  padding: 60px 20px;
  text-align: center;
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.desktop-empty-content {
  max-width: 300px;
}

.desktop-empty-icon {
  font-size: 48px;
  color: #94a3b8;
  margin-bottom: 16px;
}

.desktop-empty-text {
  font-size: 16px;
  color: #64748b;
  margin-bottom: 24px;
  font-weight: 500;
}

.desktop-empty-btn {
  min-height: 44px;
  padding: 12px 24px;
  font-weight: 600;

  .feather {
    font-size: 16px;
  }
}

/* 모바일용 빈 목록 안내 메시지 */
.mobile-empty-message {
  padding: 60px 20px;
  text-align: center;
  background: #f8fafc;
  border: 2px dashed #cbd5e1;
  border-radius: 12px;
  margin-bottom: 20px;
}

.mobile-empty-content {
  max-width: 300px;
  margin: 0 auto;
}

.mobile-empty-icon {
  font-size: 48px;
  color: #94a3b8;
  margin-bottom: 16px;
}

.mobile-empty-text {
  font-size: 16px;
  color: #64748b;
  margin-bottom: 24px;
  font-weight: 500;
}

.mobile-empty-btn {
  min-height: 44px;
  padding: 12px 24px;
  font-weight: 600;

  .feather {
    font-size: 16px;
  }
}

/* Desktop Table Styles */
.desktop-table-header,
.desktop-table-row {
  display: grid;
  grid-template-columns: 60px 1fr 1fr 1fr 1.2fr;
  border-bottom: $border;
}

.desktop-table-header {
  background-color: $bg;
}

.desktop-table-row {
  border-bottom: $border;
}

.checkbox-col {
  width: 60px;
  min-width: 60px;
  max-width: 60px;
}

.content-col {
  min-width: 120px;
}

.content-col-wide {
  min-width: 150px;
}

.row-header {
  background-color: $bg;
  text-align: center;

  &__checkbox-title {
    height: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    border-bottom: $border;
    width: 100%;
  }
  &__checkboxs {
    display: flex;
    height: 50%;
    width: 100%;
    div {
      flex: 1;
      height: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
    }
    div:nth-child(2) {
      border-left: $border;
      border-right: $border;
    }
  }
}
.row-header__item {
  border-top: $border;
  border-bottom: $border;
  border-left: none;
  border-right: none;
  padding-top: 10px;
  padding-bottom: 10px;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}
.row-header__item.item-col {
  flex-direction: column;
}
.row-header__item:last-child {
  border-right: none;
}

.none-border-center {
  padding-top: 10px;
  padding-bottom: 10px;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

/* row data */
.row-content {
  border-bottom: $border;
  &__title {
    background-color: $bg;
    height: 100%;
  }
  &__body {
  }
}
.row-content__body-item {
  .item-content {
    padding: 15px;
    border-left: $border;

    pre {
      font-family: "Noto Sans KR", "Montserrat", Helvetica, Arial, sans-serif;
      white-space: pre-wrap;
    }
  }
  .item-box {
    border-left: $border;
    border-right: $border;
    display: flex;
    .item-box-check {
      flex: 1;
      justify-content: center !important;
    }
    .item-box-check:nth-child(2) {
      border-left: $border;
      border-right: $border;
    }
  }
  .item-area {
    padding: 5px;
    align-self: center;
  }
}
.row-content__body-item ~ .row-content__body-item {
  border-top: $border;
}
.vs-component.vs-con-textarea.vs-textarea-primary {
  margin: 0;
}
.row-content-none-data {
  height: 120px;
  border-bottom: $border;
  display: flex;
  justify-content: center;
  align-items: center;
}

/* Mobile Styles */
.mobile-header {
  display: flex;
  align-items: center;
  padding: 15px;
  background-color: $bg;
  border: $border;
  border-radius: 8px;
  margin-bottom: 15px;
  gap: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;

  &:hover {
    background-color: #f1f5f9;
    border-color: #94a3b8;
  }

  &:active {
    transform: scale(0.98);
  }

  .mobile-header-text {
    font-weight: 500;
    font-size: 16px;
    color: #374151;
    flex: 1;
  }

  .vs-checkbox {
    transform: scale(1.2);
    pointer-events: none;
  }
}

.mobile-cards-container {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.mobile-card {
  background: $mobile-card-bg;
  border: $mobile-card-border;
  border-radius: 12px;
  box-shadow: $mobile-card-shadow;
  overflow: hidden;
  transition: all 0.3s ease;

  &.selected {
    background: $mobile-selected-bg;
    border: $mobile-selected-border;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
  }

  &.print-mode {
    box-shadow: none;
    border: $border;
    margin-bottom: 10px;
  }
}

.mobile-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 15px 20px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-bottom: 1px solid #e2e8f0;

  &.print-header {
    background: $bg;
    justify-content: center;
  }

  .mobile-card-header-left {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .card-index {
    font-weight: 600;
    font-size: 16px;
    color: #1e293b;
  }

  .vs-checkbox {
    transform: scale(1.2);
  }

  .mobile-delete-btn {
    min-height: 32px;
    padding: 8px 12px;
    font-size: 12px;
    font-weight: 500;
    border-radius: 6px;

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 2px 6px rgba(220, 38, 38, 0.3);
    }
  }
}

.mobile-card-content {
  padding: 20px;
}

.mobile-field {
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 0;
  }
}

.mobile-field-row {
  display: flex;
  gap: 15px;
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 0;
  }
}

.mobile-field-half {
  flex: 1;
  margin-bottom: 0 !important;
}

.mobile-field-label {
  display: block;
  font-weight: 600;
  font-size: 14px;
  color: #374151;
  margin-bottom: 8px;
  line-height: 1.4;
}

.mobile-field-input {
  .vs-input {
    min-height: 44px;

    .vs-input__input {
      font-size: 16px;
      padding: 12px 16px;
      border-radius: 8px;
      border: 1px solid #d1d5db;

      &:focus {
        border-color: #3b82f6;
        box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
      }
    }
  }
}

.mobile-print-value {
  display: block;
  padding: 12px 16px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  color: #374151;
  min-height: 20px;
}

/* 모바일 하단 항목 추가 버튼 */
.mobile-add-button-container {
  margin-top: 20px;
  padding: 0 5px;
}

.mobile-add-btn {
  width: 100%;
  min-height: 50px;
  font-size: 16px;
  font-weight: 600;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  .feather {
    font-size: 18px;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(59, 130, 246, 0.3);
  }
}

/* 삭제 확인 팝업 스타일 */
.popup-content {
  padding: 20px;
  text-align: center;
}

.delete-message {
  font-size: 16px;
  color: #374151;
  margin-bottom: 25px;
  line-height: 1.5;
}

.popup-buttons {
  display: flex;
  gap: 15px;
  justify-content: center;

  .vs-button {
    min-width: 100px;
    min-height: 40px;
    font-weight: 600;
  }
}

@media (max-width: $mobile-breakpoint) {
  .desktop-layout {
    display: none;
  }
  .mobile-layout {
    display: block;
  }

  /* 모바일에서 항목추가 버튼 숨김 */
  .desktop-only {
    display: none !important;
  }

  /* 모바일 헤더 최적화 */
  .header-flex {
    .header-buttons {
      .vs-button {
        min-height: 40px;
        font-size: 14px;
        padding: 8px 16px;
      }
    }
  }

    /* 빈 목록 메시지 모바일 최적화 */
  .mobile-empty-message {
    padding: 40px 16px;
    margin-bottom: 16px;
  }

  .mobile-empty-icon {
    font-size: 40px;
  }

  .mobile-empty-text {
    font-size: 15px;
  }

  /* 모바일 두 컬럼 레이아웃 조정 */
  .mobile-field-row {
    flex-direction: column;
    gap: 20px;
  }

  .mobile-field-half {
    flex: none;
  }
}

/* 인쇄 시 PC 레이아웃 강제 적용 */
@media print {
  .desktop-layout {
    display: block !important;
  }
  .mobile-layout {
    display: none !important;
  }

  /* 인쇄 시 체크박스 컬럼 숨김 */
  .desktop-table-header,
  .desktop-table-row {
    grid-template-columns: 1fr 1fr 1fr 1.2fr !important;
  }

  /* 체크박스 컬럼 완전히 숨김 */
  .checkbox-col {
    display: none !important;
  }

  /* 테이블 인쇄 최적화 */
  .desktop-table-header,
  .desktop-table-row {
    break-inside: avoid;
    page-break-inside: avoid;
  }
}

/* 작은 모바일 화면 최적화 */
@media (max-width: 480px) {
  .mobile-card-content {
    padding: 15px;
  }

  .mobile-header {
    padding: 12px;
  }

  .mobile-card-header {
    padding: 12px 15px;

    .mobile-card-header-left {
      .card-index {
        font-size: 14px;
      }
    }

    .mobile-delete-btn {
      min-height: 28px;
      padding: 6px 10px;
      font-size: 11px;
    }
  }

  .mobile-add-btn {
    min-height: 48px;
    font-size: 15px;

    .feather {
      font-size: 16px;
    }
  }

  .popup-buttons {
    flex-direction: column;
    gap: 10px;

    .vs-button {
      width: 100%;
    }
  }
}

/* 태블릿 화면 (768px ~ 1024px) 두 컬럼 레이아웃 최적화 */
@media (min-width: 481px) and (max-width: 1024px) {
  .mobile-field-row {
    display: flex;
    gap: 15px;
    flex-direction: row;
  }

  .mobile-field-half {
    flex: 1;
  }
}
</style>

